import React, { useState, useEffect, useCallback } from 'react';
import { getVersions, restoreVersion } from '../../services/notesService';

/**
 * VersionHistory — Slide-over panel showing note version timeline with restore.
 */
export default function VersionHistory({ noteId, currentVersion, onRestore, onClose }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(null);

    useEffect(() => {
        if (!noteId) return;
        let cancelled = false;
        setLoading(true);
        getVersions(noteId, 30)
            .then((res) => {
                if (cancelled) return;
                const data = res?.data || res;
                setVersions(data?.versions || []);
            })
            .catch((err) => console.error('[VersionHistory]', err))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [noteId]);

    const handleRestore = useCallback(async (version) => {
        if (!window.confirm(`Restore to version ${version}? Current content will be saved as a new version.`)) return;
        try {
            setRestoring(version);
            const res = await restoreVersion(noteId, version);
            const data = res?.data || res;
            onRestore?.(data);
        } catch (err) {
            console.error('[VersionHistory] restore failed:', err);
            alert('Failed to restore version.');
        } finally {
            setRestoring(null);
        }
    }, [noteId, onRestore]);

    const formatDate = (d) => {
        if (!d) return '';
        const date = new Date(d);
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
            ' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="notes-version-overlay" onClick={onClose}>
            <div className="notes-version-panel" onClick={(e) => e.stopPropagation()}>
                <div className="notes-version-header">
                    <h3>Version History</h3>
                    <span className="notes-version-current">Current: v{currentVersion}</span>
                    <button className="notes-version-close" onClick={onClose}>&times;</button>
                </div>

                <div className="notes-version-list">
                    {loading ? (
                        <div className="notes-version-loading">Loading versions...</div>
                    ) : versions.length === 0 ? (
                        <div className="notes-version-empty">No previous versions yet.</div>
                    ) : (
                        versions.map((v) => (
                            <div key={v.version} className="notes-version-item">
                                <div className="notes-version-info">
                                    <span className="notes-version-num">v{v.version}</span>
                                    <span className="notes-version-date">{formatDate(v.timestamp)}</span>
                                    {v.changeDescription && (
                                        <span className="notes-version-desc">{v.changeDescription}</span>
                                    )}
                                    {v.sizeBytes && (
                                        <span className="notes-version-size">
                                            {(v.sizeBytes / 1024).toFixed(1)} KB
                                        </span>
                                    )}
                                </div>
                                <button
                                    className="notes-version-restore-btn"
                                    onClick={() => handleRestore(v.version)}
                                    disabled={restoring === v.version}
                                >
                                    {restoring === v.version ? 'Restoring...' : 'Restore'}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
